import { Fragment, useMemo, useState } from 'react';
import { useI18n, formatDay } from '../../i18n';
import ModalBackdrop from '../shared/ModalBackdrop';
import ModalHeader from '../shared/ModalHeader';
import ModalBody from '../shared/ModalBody';
import ModalFooter from '../shared/ModalFooter';
import Field from '../shared/Field';
import { WarningIcon } from '../shared/Icons';
import BoutStatusPill from '../bout/BoutStatusPill';
import { personName } from '../../utils/personName';
import { MAX_SIDE_JUDGES, MIN_SIDE_JUDGES } from '../../utils/tournament';
import type { Bout, Registration, Ring, TournamentReferee } from '../../types';

interface Props {
  rings: Ring[];
  bouts: Bout[];
  referees: TournamentReferee[];
  /** #589 — the registrations of the tournament: the team of each corner. */
  registrations: Registration[];
  isMultiDay: boolean;
  onClose: () => void;
}

const collator = new Intl.Collator('lt');

/** #588 — the officials that the bouts of one ring have now, from a plan or
 *  from a manual edit. Read-only. The bout list gives the ring referee and
 *  the ids of the side judges, and the referee pool gives their names. The
 *  bout list does not give the jury (#589).
 *
 *  #589 — the modal marks an official who belongs to the team of a boxer of
 *  the bout. The plan treats an empty corner as neutral, and a bracket fills
 *  the corner later, so the modal computes the mark on each render: the team
 *  of the pool member (`team_id`, which GET /referees gives only to a manager
 *  of the tournament, an admin or a jury account) against the team of the
 *  registration of each corner (the public registration list). No payload
 *  gets a new field. */
export default function ViewOfficialsModal({ rings, bouts, referees, registrations, isMultiDay, onClose }: Props) {
  const { t } = useI18n();
  const ringsWithBouts = rings.filter(r => bouts.some(b => b.ring_id === r.id));
  const [ring, setRing] = useState(ringsWithBouts[0]?.id ?? '');
  const poolNames = useMemo(() => new Map(referees.map(r => [r.id, personName(r.first_name, r.last_name)])), [referees]);
  const judgeName = (id: string) => poolNames.get(id) ?? t.viewOfficials.notInPool;
  const poolTeams = useMemo(() => new Map(referees.map(r => [r.id, r.team_id ?? null])), [referees]);
  const cornerTeams = useMemo(() => new Map(registrations.map(r => [r.athlete_record_id, r.team_id ?? null])), [registrations]);
  /** The official belongs to the team of a boxer of the bout. An empty corner is neutral. */
  const conflicted = (b: Bout, userId: string | null | undefined): boolean => {
    const team = userId ? poolTeams.get(userId) : null;
    if (!team) return false;
    return [b.red_athlete_record_id, b.blue_athlete_record_id]
      .some(record => !!record && cornerTeams.get(record) === team);
  };
  /** The conflict mark of Plan Officials: the same icon and the same tooltip. */
  const mark = (conflict: boolean) => conflict && <WarningIcon className="w-3.5 h-3.5 shrink-0 text-amber-700" />;
  // The order of the bout list: the day, then the bout number.
  const rows = bouts
    .filter(b => b.ring_id === ring)
    .sort((a, b) => (a.bout_date ?? '').localeCompare(b.bout_date ?? '') || a.bout_number - b.bout_number);
  // #599 — a bout seats 3 to 5 side judges: one column for each seat that a bout of the ring uses.
  const judgeCols = Math.min(MAX_SIDE_JUDGES, Math.max(MIN_SIDE_JUDGES, ...rows.map(b => (b.side_judges ?? []).length)));

  const corner = (first?: string | null, last?: string | null, team?: string | null) => {
    const name = personName(first, last);
    if (!name) return t.common.tbd;
    return <>{name}{team && <span className="text-gray-400">{` · ${team}`}</span>}</>;
  };
  const status = (b: Bout) => <BoutStatusPill status={b.status} size="xs" plainWhenPending />;

  return (
    <ModalBackdrop onClose={onClose} maxWidth="2xl" layout="panel" ariaLabel={t.viewOfficials.ariaLabel}>
      <ModalHeader title={t.viewOfficials.title} />
      <ModalBody className="space-y-4">
        <Field label={t.common.ring}>
          <select id="view-officials-ring" className="input" value={ring} onChange={e => setRing(e.target.value)}
            data-testid="view-officials-ring">
            {ringsWithBouts.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </Field>
        {rows.length === 0 ? (
          <p className="text-sm text-gray-400">{t.viewOfficials.noBouts}</p>
        ) : (
          <>
          {/* #631, F4 — below 640 px each bout is a block: the table of 560 px
              hid Judge 2, Judge 3 and the status on a phone. Both forms are in
              the modal; CSS shows one. The block form has its own test ids. */}
          <ul className="sm:hidden border border-gray-200 rounded-lg divide-y divide-gray-100 text-xs" data-testid="view-officials-blocks">
            {rows.map((b, i) => {
              const newDay = isMultiDay && (i === 0 || rows[i - 1].bout_date !== b.bout_date);
              const judges = (b.side_judges ?? []).map(s => s.user_id)
                .sort((x, y) => collator.compare(judgeName(x), judgeName(y)));
              const ringConflict = conflicted(b, b.ring_referee_id);
              return (
                <Fragment key={b.id}>
                  {newDay && <li className="bg-gray-50 px-3 py-1 font-semibold text-gray-600">{formatDay(b.bout_date, 'EEE dd MMM')}</li>}
                  <li data-testid={`view-officials-block-${b.bout_number}`} className={`px-3 py-2 space-y-1 ${b.status === 'scored' ? 'text-gray-400' : ''}`}>
                    <div className="flex items-start gap-2">
                      <span className="font-bold shrink-0 w-6">{b.bout_number}</span>
                      <div className="min-w-0 flex-1 break-words">
                        <div className="text-red-700">{corner(b.red_first_name, b.red_last_name, b.red_team_name)}</div>
                        <div className="text-blue-700">{corner(b.blue_first_name, b.blue_last_name, b.blue_team_name)}</div>
                      </div>
                      <span className="shrink-0">{status(b)}</span>
                    </div>
                    <div className="pl-8 flex items-start gap-1" data-testid="view-officials-block-ring-ref"
                      data-conflict={ringConflict ? 'true' : 'false'} title={ringConflict ? t.planOfficials.conflictTip : undefined}>
                      <span className="text-gray-500 shrink-0">{t.bout.ringReferee}:</span>
                      {mark(ringConflict)}
                      <span className="font-semibold break-words min-w-0">{personName(b.ring_ref_first, b.ring_ref_last) || '—'}</span>
                    </div>
                    <div className="pl-8 flex flex-wrap items-start gap-x-1">
                      <span className="text-gray-500 shrink-0">{t.bout.sideJudges}:</span>
                      {judges.length === 0 ? <span>—</span> : judges.map((id, k) => {
                        const conflict = conflicted(b, id);
                        return (
                          <span key={id} data-testid="view-officials-block-judge" data-conflict={conflict ? 'true' : 'false'}
                            title={conflict ? t.planOfficials.conflictTip : undefined}
                            className={`inline-flex items-start gap-1 ${conflict ? 'font-semibold' : ''}`}>
                            {mark(conflict)}{judgeName(id)}{k < judges.length - 1 ? ',' : ''}
                          </span>
                        );
                      })}
                    </div>
                  </li>
                </Fragment>
              );
            })}
          </ul>
          <div className="hidden sm:block border border-gray-200 rounded-lg overflow-x-auto">
            <table className="w-full min-w-[560px] text-xs">
              <thead>
                <tr className="bg-gray-50 text-gray-500 text-[10px] uppercase">
                  <th className="px-2 py-1.5 text-left">#</th>
                  <th className="px-2 py-1.5 text-left">{t.viewOfficials.colBout}</th>
                  <th className="px-2 py-1.5 text-left">{t.planOfficials.colRingRef}</th>
                  {Array.from({ length: judgeCols }, (_, i) => i + 1).map(n => (
                    <th key={n} className="px-2 py-1.5 text-left">{t.planOfficials.colJudge.replace('{n}', String(n))}</th>
                  ))}
                  <th className="px-2 py-1.5 text-left">{t.viewOfficials.colStatus}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((b, i) => {
                  const newDay = isMultiDay && (i === 0 || rows[i - 1].bout_date !== b.bout_date);
                  const judges = (b.side_judges ?? []).map(s => s.user_id)
                    .sort((x, y) => collator.compare(judgeName(x), judgeName(y)));
                  const ringConflict = conflicted(b, b.ring_referee_id);
                  return (
                    <Fragment key={b.id}>
                      {newDay && (
                        <tr className="bg-gray-50">
                          <td colSpan={4 + judgeCols} className="px-2 py-1 font-semibold text-gray-600">{formatDay(b.bout_date, 'EEE dd MMM')}</td>
                        </tr>
                      )}
                      <tr data-testid={`view-officials-row-${b.bout_number}`} className={b.status === 'scored' ? 'text-gray-400' : ''}>
                        <td className="px-2 py-1.5 font-bold">{b.bout_number}</td>
                        <td className="px-2 py-1.5">
                          <div className="text-red-700">{corner(b.red_first_name, b.red_last_name, b.red_team_name)}</div>
                          <div className="text-blue-700">{corner(b.blue_first_name, b.blue_last_name, b.blue_team_name)}</div>
                        </td>
                        <td className="px-2 py-1.5 font-semibold" data-testid="view-officials-ring-ref"
                          data-conflict={ringConflict ? 'true' : 'false'} title={ringConflict ? t.planOfficials.conflictTip : undefined}>
                          <span className="inline-flex items-start gap-1">
                            {mark(ringConflict)}
                            <span className="break-words">{personName(b.ring_ref_first, b.ring_ref_last) || '—'}</span>
                          </span>
                        </td>
                        {Array.from({ length: judgeCols }, (_, k) => k).map(k => {
                          const conflict = conflicted(b, judges[k]);
                          return (
                            <td key={k} className={`px-2 py-1.5 ${conflict ? 'font-semibold' : ''}`} data-testid="view-officials-judge"
                              data-conflict={conflict ? 'true' : 'false'} title={conflict ? t.planOfficials.conflictTip : undefined}>
                              <span className="inline-flex items-start gap-1">
                                {mark(conflict)}
                                <span className="break-words">{judges[k] ? judgeName(judges[k]) : '—'}</span>
                              </span>
                            </td>
                          );
                        })}
                        <td className="px-2 py-1.5">{status(b)}</td>
                      </tr>
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </ModalBody>
      <ModalFooter onCancel={onClose} cancelLabel={t.common.close} />
    </ModalBackdrop>
  );
}
